import { V } from "../misc/Vector.js";
import { vec2 } from "gl-matrix";
import { GeometryUtils as G } from "../utils/Geometry.js";
import { PointCandidateTrajectory } from "./PointCandidateTrajectory.js";
import { CandidateTrajectory } from "./CandidateTrajectory.js";
import { FullTrajectory } from "./FullTrajectory.js";
import { EndTrajectory } from "./EndTrajectory.js";
import Flatten from "@flatten-js/core";
export { Waypoint };
class Waypoint {
    constructor({ id, position, minDistance, innerSpacingFactor = 1, rotation = undefined, trajectories }) {
        this.id = id !== null && id !== void 0 ? id : Waypoint.nonce;
        this.position = position;
        this.minDistance = minDistance;
        this.innerSpacingFactor = innerSpacingFactor;
        this.rotation = rotation;
        this.trajectories = trajectories !== null && trajectories !== void 0 ? trajectories : [];
    }
    static get nonce() {
        return Waypoint._nonce++;
    }
    get influenceRadius() {
        return this.trajectories.length === 0 ? this.minDistance : this.trajectories[this.trajectories.length - 1].outerSpacing;
    }
    get outerWidth() {
        return this.trajectories.length === 0 ? 0 : this.trajectories[this.trajectories.length - 1].outerWidth;
    }
    get minX() {
        return this.position[0] - this.influenceRadius;
    }
    get maxX() {
        return this.position[0] + this.influenceRadius;
    }
    get minY() {
        return this.position[1] - this.influenceRadius;
    }
    get maxY() {
        return this.position[1] + this.influenceRadius;
    }
    get tangents() {
        return this.trajectories.map(t => {
            if (t instanceof PointCandidateTrajectory) {
                return undefined;
            }
            else if (t instanceof CandidateTrajectory) {
                return [t.tangent];
            }
            else if (t instanceof EndTrajectory) {
                return [t.tangent];
            }
            else if (t instanceof FullTrajectory) {
                return t.tangents;
            }
            else {
                throw new Error('Unexpected case');
            }
        }).filter(e => e !== undefined).flat();
    }
    clone() {
        return new Waypoint({
            id: this.id,
            position: this.position,
            minDistance: this.minDistance,
            innerSpacingFactor: this.innerSpacingFactor,
            rotation: this.rotation,
            trajectories: [...this.trajectories]
        });
    }
    emptyClone() {
        return new Waypoint({
            id: this.id,
            position: this.position,
            minDistance: this.minDistance,
            innerSpacingFactor: this.innerSpacingFactor,
            rotation: this.rotation,
            trajectories: []
        });
    }
    isPortWaypoint({ port, connection }) {
        const minDistance = connection.minBendradius;
        const vector = [this.position[0] - port.position[0], this.position[1] - port.position[1]];
        const distance = Math.hypot(vector[0], vector[1]);
        const dot = Math.abs(vec2.dot(vector, port.direction));
        return Math.abs(dot) < 1e-6 && distance + 1e-6 >= minDistance;
    }
    rotationOfPoint({ point, direction }) {
        const v = [point[0] - this.position[0], point[1] - this.position[1]];
        return G.rotation(v, direction);
    }
    distanceOnSegment(pointA, pointB) {
        const EPSILON = 1e-6;
        const closest = G.closestPointOnSegmentWithRelativePosition(this.position, pointA, pointB);
        if (closest === undefined || closest[0] < EPSILON || closest[0] > 1 - EPSILON) {
            return undefined;
        }
        else {
            const length = V.distance(pointA, pointB);
            return closest[0] * length;
        }
    }
    segmentIntersectsTrajectories(pointA, pointB) {
        return this.trajectories.some(t => {
            if (t instanceof PointCandidateTrajectory || t instanceof CandidateTrajectory) {
                return false;
            }
            else if (t instanceof FullTrajectory || t instanceof EndTrajectory) {
                return t.intersects(pointA, pointB);
            }
            else {
                throw new Error('Unexpected case');
            }
        });
    }
    arcIntersectsTrajectories(arc, exclude) {
        if (arc instanceof Flatten.Arc) {
            return this.trajectories.some(t => {
                if (t === exclude || t instanceof PointCandidateTrajectory || t instanceof CandidateTrajectory) {
                    return false;
                }
                else if (t instanceof FullTrajectory || t instanceof EndTrajectory) {
                    return t.arcIntersects(arc);
                }
                else {
                    throw new Error('Unexpected case');
                }
            });
        }
        else {
            return false;
        }
    }
    isIntersectingSegment(pointA, pointB, width, spacing) {
        const EPSILON = 1e-6;
        const widthHalf = 0.5 * width;
        const center = new Flatten.Point(this.position[0], this.position[1]);
        const segment = new Flatten.Segment(new Flatten.Point(pointA[0], pointA[1]), new Flatten.Point(pointB[0], pointB[1]));
        const distance = segment.distanceTo(center)[0];
        const minDistance = Math.max(this.minDistance, this.innerSpacingFactor * spacing) + widthHalf;
        if (minDistance - EPSILON > distance) {
            return true;
        }
        return this.trajectories.some(t => {
            if (t instanceof PointCandidateTrajectory || t instanceof CandidateTrajectory) {
                return false;
            }
            else if (t instanceof FullTrajectory || t instanceof EndTrajectory) {
                const arc = t.getArc();
                const distanceTo = segment.distanceTo(arc);
                const trajectoryDistance = distanceTo[0];
                const minDistance = t.widthHalf + Math.max(t.spacing, spacing) + widthHalf;
                const a = new Flatten.Vector(distanceTo[1].ps, distanceTo[1].pe);
                const b = new Flatten.Vector(new Flatten.Point(pointA[0], pointA[1]), new Flatten.Point(pointB[0], pointB[1]));
                if (a.length > 1e-3 && b.length > 1e-3) {
                    const angle = a.angleTo(b);
                    const isOutside = Math.abs(angle - 0.5 * Math.PI) > 1e-2 && Math.abs(angle - 1.5 * Math.PI) > 1e-2;
                    if (isOutside) {
                        return false;
                    }
                }
                return minDistance - EPSILON > trajectoryDistance;
            }
            else {
                throw new Error('Unexpected case');
            }
            return false;
        });
    }
    isIntersectingObstacleSegment(pointA, pointB) {
        const EPSILON = 1e-6;
        const END_EPSILON = 1e0;
        const crossEPSILON = 1e-3;
        const segment = new Flatten.Segment(new Flatten.Point(pointA[0], pointA[1]), new Flatten.Point(pointB[0], pointB[1]));
        return this.trajectories.some(t => {
            if (t instanceof PointCandidateTrajectory || t instanceof CandidateTrajectory) {
                return false;
            }
            else if (t instanceof EndTrajectory) {
                const os = new Flatten.Segment(new Flatten.Point(pointA[0], pointA[1]), new Flatten.Point(pointB[0], pointB[1]));
                const arc = t.getArc();
                const [distance, shortestSegment] = arc.distanceTo(os);
                const arcPoint = [shortestSegment.ps.x, shortestSegment.ps.y];
                const shortestSegmentPoint = [shortestSegment.pe.x, shortestSegment.pe.y];
                const arcVector = V.subtract(arcPoint, t.center);
                const shortestSegmentVector = V.subtract(shortestSegmentPoint, arcPoint);
                const dot = V.dot(arcVector, shortestSegmentVector);
                if (arc instanceof Flatten.Point) {
                    return false;
                }
                if (Math.abs(dot) < EPSILON) {
                    return false;
                }
                const arcVectorNormalized = V.normalize(arcVector);
                const shortestSegmentVectorNormalized = V.normalize(shortestSegmentVector);
                const cross = V.cross(arcVectorNormalized, shortestSegmentVectorNormalized);
                if (Math.abs(cross) < crossEPSILON) {
                    return distance < t.widthHalf + t.spacing - EPSILON;
                }
                const tangentInStart = arc.tangentInStart();
                const endVector = V.rightOrthogonal([tangentInStart.x, tangentInStart.y]);
                const endLength = t.widthHalf + t.spacing - END_EPSILON;
                const endSegmentPoint1 = V.add([arc.start.x, arc.end.y], V.toLength(endVector, endLength));
                const endSegmentPoint2 = V.add([arc.start.x, arc.end.y], V.toLength(endVector, -endLength));
                const endSegment = new Flatten.Segment(new Flatten.Point(endSegmentPoint1[0], endSegmentPoint1[1]), new Flatten.Point(endSegmentPoint2[0], endSegmentPoint2[1]));
                const intersection = endSegment.intersect(os);
                return intersection.length !== 0;
            }
            else if (t instanceof FullTrajectory) {
                const arc = t.getArc();
                const trajectoryDistance = arc.distanceTo(segment)[0];
                const minDistance = t.widthHalf + t.spacing;
                return minDistance - EPSILON > trajectoryDistance;
            }
            else {
                throw new Error('Unexpected case');
            }
            return false;
        });
    }
}
Waypoint._nonce = 0;
//# sourceMappingURL=Waypoint.js.map